import "@baklavajs/themes/dist/syrup-dark.css"
import { createEffect, createMemo, createSignal, onCleanup, onMount, Show } from "solid-js"
import type { RegistryRuntime } from "./registry-block"
import {
  normalizeRegistrySubmits,
  registrySubmitByCommand,
  type RegistrySubmitDescriptor,
} from "./registry-submit-runtime"
import { normalizeVisualPipelineTemplates } from "./visual-pipeline-authoring"
import { applyBaklavaHydration, toBaklavaHydrationModel } from "./visual-pipeline-baklava"
import { VisualPipelineBlockView, type VisualPipelineBlock, type VisualPipelineGraph } from "./visual-pipeline-block"

type VisualPipelineRegistryProps = {
  title?: unknown
  description?: unknown
  mode?: unknown
  graph?: unknown
  nodeTypes?: unknown
  templates?: unknown
  validation?: unknown
  height?: unknown
  registryRuntime?: RegistryRuntime
}

type VisualPipelineValidation = NonNullable<VisualPipelineBlock["validation"]>

function isGraph(value: unknown): value is VisualPipelineGraph {
  return Boolean(
    value &&
      typeof value === "object" &&
      (value as { schemaVersion?: unknown }).schemaVersion === 1 &&
      Array.isArray((value as { nodes?: unknown }).nodes) &&
      Array.isArray((value as { edges?: unknown }).edges),
  )
}

function nodeTypes(value: unknown): VisualPipelineBlock["nodeTypes"] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is VisualPipelineBlock["nodeTypes"][number] =>
    Boolean(item && typeof item === "object" && typeof (item as { type?: unknown }).type === "string"),
  )
}

function validation(value: unknown): VisualPipelineBlock["validation"] | undefined {
  if (!value || typeof value !== "object") return undefined
  const item = value as { status?: unknown; messages?: unknown }
  if (item.status !== "valid" && item.status !== "warning" && item.status !== "error") return undefined
  return {
    status: item.status,
    messages: Array.isArray(item.messages) ? (item.messages as VisualPipelineValidation["messages"]) : [],
  }
}

type VisualPipelineCommandName = "pipeline.save" | "pipeline.export_markdown" | "pipeline.export_recipe"

function isVisualPipelineCommand(
  submit: RegistrySubmitDescriptor | undefined,
): submit is RegistrySubmitDescriptor & { command: VisualPipelineCommandName } {
  return (
    submit?.command === "pipeline.save" ||
    submit?.command === "pipeline.export_markdown" ||
    submit?.command === "pipeline.export_recipe"
  )
}

function visualPipelineCommands(runtime?: RegistryRuntime) {
  const submits = normalizeRegistrySubmits(runtime)
  const save = registrySubmitByCommand(submits, "pipeline.save")
  const markdown = registrySubmitByCommand(submits, "pipeline.export_markdown")
  const recipe = registrySubmitByCommand(submits, "pipeline.export_recipe")
  return {
    save: isVisualPipelineCommand(save) ? save : undefined,
    exports: [markdown, recipe].filter(isVisualPipelineCommand),
  }
}

export function VisualPipelineRegistry(props: VisualPipelineRegistryProps) {
  let host: HTMLDivElement | undefined
  let cleanup: (() => void) | undefined
  const [mountReady, setMountReady] = createSignal(false)
  const [error, setError] = createSignal("")
  const [hydration, setHydration] = createSignal({ warnings: [] as string[], nodeIds: [] as string[], edgeIds: [] as string[] })
  const graph = createMemo(() => (isGraph(props.graph) ? props.graph : undefined))
  const parsedNodeTypes = createMemo(() => nodeTypes(props.nodeTypes))
  const parsedTemplates = createMemo(() => normalizeVisualPipelineTemplates(props.templates, parsedNodeTypes()))
  const parsedValidation = createMemo(() => validation(props.validation))
  const model = createMemo(() => (graph() ? toBaklavaHydrationModel(graph()!, parsedNodeTypes()) : undefined))
  const commands = createMemo(() => visualPipelineCommands(props.registryRuntime))
  const height = () => {
    const value = Number(props.height ?? 520)
    if (!Number.isFinite(value)) return "520px"
    return `${Math.max(360, Math.min(900, value))}px`
  }

  async function mount() {
    cleanup?.()
    cleanup = undefined
    if (!host || !model()) return
    const vue = await import("vue")
    const renderer = await import("@baklavajs/renderer-vue")
    const component = vue.defineComponent({
      setup() {
        const baklava = renderer.useBaklava()
        baklava.settings.palette.enabled = false
        baklava.settings.sidebar.enabled = false
        baklava.settings.toolbar.enabled = false
        baklava.settings.contextMenu.enabled = false
        baklava.settings.nodes.resizable = false
        setHydration(applyBaklavaHydration({ editor: baklava.editor, model: model()!, readonly: true }))
        return () => vue.h("div", { class: "h-full oa-visual-pipeline-canvas-readonly" }, [vue.h(renderer.BaklavaEditor, { viewModel: baklava })])
      },
    })
    const app = vue.createApp(component)
    app.mount(host)
    cleanup = () => app.unmount()
    setError("")
  }

  onMount(() => setMountReady(true))

  createEffect(() => {
    if (!mountReady()) return
    if (!graph()) {
      setError("Visual pipeline registry component requires a graph prop.")
      return
    }
    void mount().catch((caught) => setError(caught instanceof Error ? caught.message : "Visual pipeline unavailable"))
  })

  onCleanup(() => cleanup?.())

  if (props.mode === "design" && props.registryRuntime && graph()) {
    return (
      <VisualPipelineBlockView
        block={{
          id: props.registryRuntime.blockId,
          type: "visual_pipeline",
          title: typeof props.title === "string" ? props.title : undefined,
          description: typeof props.description === "string" ? props.description : undefined,
          mode: "design",
          submitToolName: "",
          graph: graph()!,
          nodeTypes: parsedNodeTypes(),
          templates: parsedTemplates().templates,
          validation:
            parsedValidation() || parsedTemplates().messages.length
              ? {
                  status: parsedValidation()?.status ?? (parsedTemplates().messages.length ? "error" : "valid"),
                  messages: [...(parsedValidation()?.messages ?? []), ...parsedTemplates().messages],
                }
              : undefined,
        }}
        pending={props.registryRuntime.pending}
        readonly={props.registryRuntime.readonly}
        submitCommands={commands()}
        submitState={props.registryRuntime.submitState}
        onSubmit={(_blockId, values) => props.registryRuntime?.onSubmit(values)}
      />
    )
  }

  return (
    <section data-visual-pipeline-registry class="flex min-w-0 flex-col gap-3">
      <Show when={typeof props.title === "string" && props.title}>
        <h3 class="text-15-medium text-text-strong">{String(props.title)}</h3>
      </Show>
      <Show when={typeof props.description === "string" && props.description}>
        <p class="text-13-regular text-text-base">{String(props.description)}</p>
      </Show>
      <Show when={validation(props.validation)}>
        {(item) => (
          <div class="rounded-md border border-border-weak-base bg-background-secondary px-3 py-2 text-12-regular text-text-base">
            {item().status} / {item().messages.length} messages
          </div>
        )}
      </Show>
      <Show when={error()}>
        <div role="status" data-visual-pipeline-error class="rounded-md border border-border-weak-base bg-background-secondary px-3 py-2 text-12-regular text-text-base">
          {error()}
        </div>
      </Show>
      <div
        ref={host}
        data-visual-pipeline-canvas="registry"
        data-hydrated-nodes={hydration().nodeIds.length}
        data-hydrated-edges={hydration().edgeIds.length}
        class="overflow-hidden rounded-lg border border-border-weak-base bg-background-base"
        style={{ height: height() }}
      />
    </section>
  )
}
